import * as React from 'react'
import { duration } from 'moment'

import { BusinessTripModel } from '@/app/models/businessTripModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Tooltip from '@/modules/drivers/shared/ToolTip'

interface BusinessTripsSummaryFooterProps {
  trips: BusinessTripModel[]
}

const moneyFormatter = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const fmtMoney = (value: number): string =>
  `${moneyFormatter.format(value)} PLN`


const fmtDuration = (milliseconds: number): string => {
  const totalMinutes = Math.round(milliseconds / 60000)
  if (totalMinutes <= 0) return '-'

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0 || days > 0) parts.push(`${hours}h`)
  parts.push(`${minutes}m`)

  return parts.join(' ')
}

const KpiInline: React.FC<{
  label: string
  value: React.ReactNode
  tooltip: string
}> = ({ label, value, tooltip }) => (
  <Tooltip
    text={tooltip}
    asChild
    portal
    maxWidth="22rem"
    className="!tw-cursor-default"
  >
    <span className="tw-mb-0 tw-whitespace-nowrap tw-text-left tw-text-sm">
      <span className="tw-text-content-secondary">{label}:</span>{' '}
      <span className="tw-font-medium">{value}</span>
    </span>
  </Tooltip>
)

const Divider: React.FC = () => (
  <div className="tw-h-5 tw-w-0.5 tw-shrink-0 tw-bg-secondary" />
)

const BusinessTripsSummaryFooter: React.FC<BusinessTripsSummaryFooterProps> = ({
  trips,
}) => {
  const { t, tSafe } = useLocalize()

  const summary = React.useMemo(() => {
    const totals = {
      count: trips.length,
      timeHomeMs: 0,
      timeAwayMs: 0,
      amount: 0,
      parts: 0,
      accommodations: 0,
      invoices: 0,
      loans: 0,
    }

    for (const trip of trips) {
      totals.timeHomeMs += duration(trip.timeLengthHome).asMilliseconds() || 0
      totals.timeAwayMs += duration(trip.timeLengthAway).asMilliseconds() || 0
      totals.amount += trip.amount || 0
      totals.parts += trip.parts || 0
      totals.accommodations += trip.accommodations || 0
      totals.invoices += trip.invoices || 0
      totals.loans += trip.loans || 0
    }

    return totals
  }, [trips])

  if (!summary.count) return null

  const totalTimeMs = summary.timeHomeMs + summary.timeAwayMs

  return (
    <div className="tw-flex tw-min-h-10 tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-t tw-border-base-300 tw-bg-content-primary tw-px-4 tw-py-2">
      <KpiInline
        label={tSafe('businessTrip.summary-count', 'Delegacje')}
        value={summary.count}
        tooltip={tSafe(
          'businessTrip.tip.count',
          'Liczba delegacji w wybranym zakresie dat, z uwzglednieniem aktywnych filtrow.',
        )}
      />
      <Divider />
      <KpiInline
        label={tSafe('businessTrip.summary-total-time', 'Łączny czas')}
        value={fmtDuration(totalTimeMs)}
        tooltip={tSafe(
          'businessTrip.tip.total-time',
          'Łączny czas delegacji w zakresie: suma czasu w kraju i za granicą.',
        )}
      />
      <Divider />
      <KpiInline
        label={t('businessTrip.amount')}
        value={fmtMoney(summary.amount)}
        tooltip={tSafe(
          'businessTrip.tip.amount',
          'Suma kwot „Razem” wszystkich delegacji w zakresie (diety, noclegi i koszty).',
        )}
      />
      <Divider />
      <KpiInline
        label={t('businessTrip.parts')}
        value={fmtMoney(summary.parts)}
        tooltip={tSafe(
          'businessTrip.tip.parts',
          'Suma samych diet ze wszystkich delegacji w zakresie.',
        )}
      />
      <Divider />
      <KpiInline
        label={tSafe('businessTrip.accomodations', 'Noclegi')}
        value={fmtMoney(summary.accommodations)}
        tooltip={tSafe(
          'businessTrip.tip.accommodations',
          'Suma samych noclegów ze wszystkich delegacji w zakresie.',
        )}
      />
      <Divider />
      <KpiInline
        label={t('businessTrip.invoices')}
        value={fmtMoney(summary.invoices)}
        tooltip={tSafe(
          'businessTrip.tip.invoices',
          'Suma kosztów (faktur) ze wszystkich delegacji w zakresie.',
        )}
      />
      <Divider />
      <KpiInline
        label={t('businessTrip.loans')}
        value={fmtMoney(summary.loans)}
        tooltip={tSafe(
          'businessTrip.tip.loans',
          'Suma zaliczek ze wszystkich delegacji w zakresie.',
        )}
      />
      <Divider />
      <KpiInline
        label={t('common.time')}
        value={`${fmtDuration(summary.timeHomeMs)} / ${fmtDuration(
          summary.timeAwayMs,
        )}`}
        tooltip={tSafe(
          'businessTrip.tip.time-split',
          'Czas w kraju / czas za granicą - w tej samej kolejności co kolumna „Czas” w tabeli.',
        )}
      />
    </div>
  )
}

export default BusinessTripsSummaryFooter
